<script setup lang="ts">
  import { logoWhite, logo } from '@gat-ship/vue-components'
  import {
    Popover,
    PopoverContent,
    PopoverTrigger
  } from '@gat-ship/vue-components/popover'
  import { computed, ref } from 'vue'
  import {
    routeDefinitions,
    useRouter,
    appRouteDefinitions
  } from '@gat-ship/lib/router'
  import { useUser } from '@gat-ship/lib'
  import { SidebarMenuItem, SidebarProvider } from '../../sidebar'
  import { useAppShell } from './app-shell.vue'
  import {
    Avatar,
    AvatarImage,
    AvatarFallback
  } from '@gat-ship/vue-components/avatar'
  import { accessCodes } from '@gat-ship/types'
  import { globalUtils } from '@gat-ship/utils'

  const router = useRouter()
  const theme = globalUtils.composables.useTheme()

  const { isRail, toggleRail, mainMenuContent } = useAppShell({
    isRail: ref(false),
    toggleRail: () => {},
    mainMenuContent: ref(null),
    setMainMenuContent: () => {
      console.log('called from sidebar :/')
    },
    subMenuContent: ref(null),
    setSubMenuContent: () => {
      console.log('called from sidebar :/')
    }
  })

  const {
    databases: availableDatabases,
    switchDatabase,
    cacheInvalidation,
    userProfile,
    isTenantAdmin,
    hasPermission,
    userPreferences
  } = useUser()

  const clearCacheText = computed(() => {
    if (cacheInvalidation.isCooldownActive.value) {
      return `Clear cache (${cacheInvalidation.remainingCooldownSeconds.value}s)`
    }
    return 'Clear cache'
  })

  async function handleClearCache() {
    try {
      await cacheInvalidation.invalidateCache()
    } catch (error) {
      console.error('Failed to clear cache:', error)
      // You might want to show a toast notification here
    }
  }

  const userProfileImageUrl = computed(() => {
    return (
      userProfile.value?.actor?.imageUrl ?? userProfile.value?.profileImageUrl
    )
  })

  function getEnvironment() {
    return import.meta.env.MODE
  }

  const canAccessAdministration = computed(() => {
    return (
      isTenantAdmin.value ||
      hasPermission(accessCodes.system.currency.manageCurrencies) ||
      hasPermission(accessCodes.system.agency.viewAgencyStructure)
    )
  })

  const isProfilePopoverOpen = ref(false)
</script>
<template>
  <sidebar-provider :is-rail="isRail">
    <div
      class="transition-all-default border-border bg-bg-white-0 left-0 top-0 z-40 h-full w-full overflow-visible border-r duration-0"
    >
      <div class="relative z-50 flex h-full w-full flex-col">
        <div
          class="absolute -right-1.5 top-1/2 cursor-pointer"
          @click="toggleRail()"
        >
          <div
            class="bg-background-lighter flex h-6 w-3 items-center justify-center rounded-sm border dark:bg-neutral-800"
          >
            <gat-icon
              :icon="$icons.common.ChevronLeft"
              class="size-4"
              :class="{ 'rotate-180': isRail }"
            />
          </div>
        </div>
        <div
          class="flex min-h-[88px] items-center"
          :class="{ 'p-3': !isRail, 'p-1': isRail }"
        >
          <!-- <Popover v-model:open="isAppChooserOpen">
            <PopoverTrigger as-child> -->
          <button
            type="button"
            class="transition-all-default flex w-full items-center gap-3 whitespace-nowrap p-3 text-left outline-none focus:outline-none"
          >
            <img v-if="theme.isDark.value" :src="logoWhite" class="size-10" />
            <img v-else :src="logo" class="size-10" />

            <div class="flex w-full items-center gap-3" v-if="!isRail">
              <div class="flex-1 space-y-1">
                <div class="text-base leading-tight">Gatship</div>
                <div class="text-soft text-xs capitalize leading-none">
                  {{ getEnvironment() }}
                </div>
              </div>
              <!-- <div
                class="border-stroke-soft-200 shadow-regular-xs flex size-6 items-center justify-center rounded-md border"
              >
                <gat-icon
                  :icon="$icons.common.ChevronUpDown"
                  class="text-soft size-4"
                />
              </div> -->
            </div>
          </button>
          <!-- </PopoverTrigger>
            <PopoverContent
              as-child
              align="start"
              class="p-0"
              side="right"
              :side-offset="24"
            >
              <div class="flex flex-col gap-2 p-2">
                <button
                  v-if="
                    $route.path.includes('mail') ||
                    $route.path.includes('admin')
                  "
                  type="button"
                  class="group/item hover:bg-accent/50 flex w-full cursor-pointer items-center gap-3 rounded-sm p-2 text-left outline-none transition duration-200 ease-out hover:cursor-pointer focus:outline-none"
                  @click="[
                    (isAppChooserOpen = false),
                    router.push({ name: appRouteDefinitions.root })
                  ]"
                >
                  <div
                    class="shadow-xs ring-muted flex size-10 items-center justify-center rounded-full ring-1 ring-inset"
                  >
                    <gat-icon
                      :icon="$icons.common.ChevronLeft"
                      class="size-4"
                    />
                  </div>
                  <div class="flex-1 space-y-1">
                    <div class="text-sm leading-tight">Main</div>
                    <div class="text-secondary-foreground text-xs leading-none">
                      Main Application
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  class="group/item hover:bg-accent/50 flex w-full cursor-pointer items-center gap-3 rounded-sm p-2 text-left outline-none transition duration-200 ease-out hover:cursor-pointer focus:outline-none"
                  @click="[
                    (isAppChooserOpen = false),
                    router.push({ name: routeDefinitions.mail.mailInbox })
                  ]"
                >
                  <div
                    class="shadow-xs ring-muted flex size-10 items-center justify-center rounded-full ring-1 ring-inset"
                  >
                    <gat-icon :icon="$icons.common.Mail" class="size-4" />
                  </div>
                  <div class="flex-1 space-y-1">
                    <div class="text-sm leading-tight">Mail</div>
                    <div class="text-secondary-foreground text-xs leading-none">
                      Work in progress
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  class="group/item hover:bg-accent/50 flex w-full cursor-pointer items-center gap-3 rounded-sm p-2 text-left outline-none transition duration-200 ease-out hover:cursor-pointer focus:outline-none"
                  @click="[
                    (isAppChooserOpen = false),
                    router.push({
                      name: routeDefinitions.administration.tenant.tenantUsers
                    })
                  ]"
                >
                  <div
                    class="shadow-xs ring-muted flex size-10 items-center justify-center rounded-full ring-1 ring-inset"
                  >
                    <gat-icon
                      :icon="$icons.menuIcons.AdminMenu"
                      class="size-4"
                    />
                  </div>
                  <div class="flex-1 space-y-1">
                    <div class="text-sm leading-tight">Administration</div>
                    <div class="text-secondary-foreground text-xs leading-none">
                      Administer your gat-ship data
                    </div>
                  </div>
                </button>
              </div>
            </PopoverContent>
          </Popover> -->
        </div>
        <div class="px-5">
          <div
            role="separator"
            class="before:bg-border transition-all-default relative flex h-0 w-full items-center before:absolute before:left-0 before:top-1/2 before:h-px before:w-full before:-translate-y-1/2"
          ></div>
        </div>
        <div
          class="flex w-full flex-1 flex-col gap-0 gap-1.5 overflow-auto pb-4 pt-5"
          id="sidebar-menu-items"
          :class="{
            'w-fit items-center': isRail,
            'w-full items-start px-5': !isRail
          }"
        >
          <component v-if="mainMenuContent" :is="mainMenuContent" />
          <!-- <div class="px-5" v-if="mainMenuContent">
          <div
            role="separator"
            class="before:bg-border transition-all-default relative flex h-0 w-full items-center before:absolute before:left-0 before:top-1/2 before:h-px before:w-full before:-translate-y-1/2"
          ></div>
        </div> -->
          <slot v-else name="main-menu-content" />
          <div
            class="mt-auto flex w-full flex-col gap-0.5"
            :class="{ 'w-fit items-center': isRail }"
          >
            <!-- <div class="text-soft p-1 text-xs uppercase">Others</div> -->
            <!-- <div class="space-y-1" > -->
            <sidebar-menu-item
              v-if="canAccessAdministration"
              :to="{ name: routeDefinitions.administration.tenant.tenantUsers }"
              text="Administration"
              :icon="$icons.menuIcons.AdminMenu"
            />
            <sidebar-menu-item
              :to="{ name: routeDefinitions.support.list }"
              text="Support"
              :icon="$icons.support.support"
            />
            <!-- </div> -->
          </div>
        </div>
        <div class="px-5">
          <div
            role="separator"
            class="before:bg-border transition-all-default relative flex h-0 w-full items-center before:absolute before:left-0 before:top-1/2 before:h-px before:w-full before:-translate-y-1/2"
          ></div>
        </div>
        <div
          class="flex w-full flex-col items-center justify-center overflow-hidden p-3"
        >
          <Popover v-model:open="isProfilePopoverOpen">
            <PopoverTrigger as-child>
              <button
                type="button"
                id="radix-:r2:"
                aria-haspopup="menu"
                aria-expanded="false"
                data-state="closed"
                class="hover:bg-accent/50 transition-all-default flex items-center gap-3 whitespace-nowrap rounded-sm outline-none transition hover:cursor-pointer focus:outline-none"
                :class="{
                  'w-fit p-0': isRail,
                  'w-full p-3 text-left': !isRail
                }"
              >
                <div
                  class="relative flex shrink-0 select-none items-center justify-center rounded-full text-center uppercase"
                  :class="{
                    'size-8': isRail,
                    'size-10': !isRail,
                    'border-support-session border-2':
                      $user.isSupportSession.value
                  }"
                >
                  <avatar>
                    <avatar-image
                      v-if="userProfileImageUrl"
                      class="size-full rounded-full object-cover"
                      :src="userProfileImageUrl"
                    />
                    <avatar-fallback>{{
                      $user.userProfile.value?.fullName.slice(0, 2)
                    }}</avatar-fallback>
                  </avatar>
                  <!-- <img
                    class="size-full rounded-full object-cover"
                    :src="
                      $user.userProfile.value?.actor?.imageUrl ??
                      $user.userProfile.value?.profileImageUrl
                    "
                    alt=""
                  /> -->
                  <img
                    class="absolute bottom-1 right-1 size-4 rounded-full object-cover opacity-80"
                    v-if="$user.isSupportSession.value"
                    :src="$user.userProfile.value?.profileImageUrl"
                    alt=""
                  />
                </div>
                <div
                  v-if="!isRail"
                  class="flex w-full items-center gap-3 overflow-hidden"
                >
                  <div
                    class="flex w-full grow flex-col space-y-1 overflow-hidden"
                  >
                    <div class="flex items-center gap-0.5 truncate text-sm">
                      {{ $user.userProfile.value?.fullName }}
                    </div>
                    <div
                      class="text-soft flex w-full items-center gap-0 overflow-hidden truncate text-xs"
                    >
                      <div class="truncate">
                        {{ $user.userProfile.value?.email.split('@')[0] }}
                      </div>
                      <span>
                        @{{ $user.userProfile.value?.email.split('@')[1] }}
                      </span>
                    </div>
                  </div>
                  <div
                    class="flex size-6 shrink-0 items-center justify-center rounded-md"
                  >
                    <gat-icon
                      :icon="$icons.common.ChevronRight"
                      class="text-soft size-4"
                    />
                  </div>
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent
              as-child
              align="end"
              class="p-0"
              side="right"
              :side-offset="24"
            >
              <div class="flex flex-col gap-1 p-2">
                <div
                  role="menuitem"
                  class="group/item hover:bg-accent/50 text-secondary-foreground relative flex cursor-pointer select-none items-center gap-2 rounded-sm p-2 text-sm capitalize outline-none transition duration-200 ease-out focus:outline-none"
                  tabindex="-1"
                  @click="
                    userPreferences.getUserPreferences().theme === 'light'
                      ? userPreferences.updateUserPreferences({ theme: 'dark' })
                      : userPreferences.updateUserPreferences({
                          theme: 'light'
                        })
                  "
                >
                  <gat-icon
                    :icon="$icons.userMenuIcons.Theme"
                    class="text-secondary-foreground size-4"
                  />
                  Dark Mode
                  <switch-tiny
                    class="ml-auto"
                    :model-value="theme.isDark.value"
                  />
                </div>
                <button
                  role="menuitem"
                  :disabled="cacheInvalidation.isCooldownActive.value"
                  class="group/item hover:bg-accent/50 text-secondary-foreground relative flex cursor-pointer select-none items-center gap-2 rounded-sm p-2 text-sm capitalize outline-none transition duration-200 ease-out focus:outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                  :data-disabled="cacheInvalidation.isCooldownActive.value"
                  tabindex="-1"
                  @click="handleClearCache()"
                >
                  <gat-icon
                    :icon="$icons.userMenuIcons.RefreshCcw"
                    class="text-secondary-foreground size-4"
                  />
                  <span>{{ clearCacheText }}</span>
                </button>
                <Separator />
                <div
                  role="menuitem"
                  class="group/item hover:bg-accent/50 text-secondary-foreground relative flex cursor-pointer select-none items-center gap-2 rounded-sm p-2 text-sm capitalize outline-none transition duration-200 ease-out focus:outline-none"
                  tabindex="-1"
                  @click="[
                    router.push({ name: routeDefinitions.user.general }),
                    (isProfilePopoverOpen = false)
                  ]"
                >
                  <gat-icon
                    :icon="$icons.profileSettings.base"
                    class="text-secondary-foreground size-4"
                  />
                  Settings
                </div>
                <Popover>
                  <PopoverTrigger as-child>
                    <button
                      role="menuitem"
                      class="group/item hover:bg-accent/50 text-secondary-foreground relative flex cursor-pointer select-none items-center gap-2 rounded-sm p-2 text-sm capitalize outline-none transition duration-200 ease-out focus:outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                      tabindex="-1"
                    >
                      <gat-icon
                        :icon="$icons.userMenuIcons.Database"
                        class="text-secondary-foreground size-4"
                      />
                      <div class="flex grow flex-col text-left">
                        <span class="leading-tight">Switch Database</span>
                        <span class="text-soft text-xs leading-none">{{
                          availableDatabases.current?.name
                        }}</span>
                      </div>
                      <gat-icon
                        :icon="$icons.common.ChevronRight"
                        class="text-secondary-foreground size-4"
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    as-child
                    align="end"
                    class="p-0"
                    side="right"
                    :side-offset="12"
                  >
                    <div class="flex flex-col gap-1 p-2">
                      <div v-for="db in availableDatabases.all" :key="db.id">
                        <button
                          role="menuitem"
                          :disabled="db.id === availableDatabases.current?.id"
                          :data-disabled="
                            db.id === availableDatabases.current?.id
                          "
                          class="group/item hover:bg-accent/50 text-secondary-foreground relative flex min-h-10 w-full cursor-pointer select-none items-center gap-2 rounded-sm p-2 text-sm capitalize outline-none transition duration-200 ease-out focus:outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
                          tabindex="-1"
                          @click="switchDatabase(db.id)"
                        >
                          <gat-icon
                            :icon="$icons.userMenuIcons.Database"
                            class="text-secondary-foreground size-4"
                          />
                          <div class="flex grow flex-col text-left">
                            <span class="leading-tight">{{ db.name }}</span>
                            <!-- <span class="text-soft text-xs leading-none">{{
                            db.tenantName
                          }}</span> -->
                          </div>
                          <gat-icon
                            v-if="db.id === availableDatabases.current?.id"
                            :icon="$icons.common.check"
                            class="text-secondary-foreground size-4"
                          />
                        </button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                <Separator />
                <div
                  role="menuitem"
                  class="group/item hover:bg-accent/50 text-secondary-foreground relative flex cursor-pointer select-none items-center gap-2 rounded-sm p-2 text-sm capitalize outline-none transition duration-200 ease-out focus:outline-none"
                  tabindex="-1"
                  @click="$auth0.logout()"
                >
                  <gat-icon
                    :icon="$icons.userMenuIcons.LogOut"
                    class="text-secondary-foreground size-4"
                  />
                  Logout
                </div>
                <div class="text-xs">
                  Build: {{ $appState.currentBuild }} ({{ getEnvironment() }})
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <!-- <div
          class="flex items-center gap-2 p-3 pt-0"
          :class="{ 'justify-center': isRail }"
        >
          <template v-if="!isRail">
            <router-link
              to="/mail"
              class="hover:bg-accent/50 relative flex size-8 items-center justify-center rounded-sm p-1 text-xs"
            >
              <gat-icon :icon="$icons.common.Mail" class="size-3.5" />
            </router-link>
            <router-link
              to="/mail"
              class="hover:bg-accent/50 relative flex size-8 items-center justify-center rounded-sm p-1 text-xs"
            >
              <gat-icon icon="Bell" class="size-3.5" />
            </router-link>
            <router-link
              to="/mail"
              class="hover:bg-accent/50 pointer-events-none relative flex size-8 items-center justify-center rounded-sm p-1 text-xs opacity-50"
            >
              <gat-icon icon="MessageSquare" class="size-3.5" />
            </router-link>
          </template>
</div> -->
      </div>
    </div>
  </sidebar-provider>
</template>
